import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Save, History, AlertTriangle, Loader2, Search,
    Building2, User, X, RotateCcw, ChevronDown,
    ChevronRight, Check, Repeat2, Minus, Columns2, AlignJustify,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Static definitions ───────────────────────────────────────────────────

const ENTITIES = [
    { key: 'company',  label: 'Company',  crm: 'account',  Icon: Building2 },
    { key: 'investor', label: 'Investor', crm: 'account',  Icon: Building2 },
    { key: 'person',   label: 'Person',   crm: 'contact',  Icon: User },
];

// Policy is a *control*, not a status — neutral stone palette only.
// Color in this UI is reserved for real states (rose = conflict/error).
const POLICY_META = {
    fill_blank: {
        label: 'Only if empty',
        short: 'Only if empty',
        // Shown on hover — both tips answer the same question:
        // "When this CRM field already has a value, what happens?"
        tip:   "Existing CRM values are kept — this field is only written when it's blank. Your manually-entered data stays safe. Click to switch.",
        color: 'text-stone-600 bg-stone-100 border-stone-200',
    },
    always: {
        label: 'Always replace',
        short: 'Always replace',
        tip:   "Existing CRM values are replaced on every import, even if you've edited them manually. Use for fields you always want in sync with PitchBook. Click to switch.",
        color: 'text-stone-700 bg-stone-200/70 border-stone-300',
    },
    ignore: {
        label: 'Ignore',
        short: 'Ignore',
        tip:   "This PitchBook column is documented here but won't write to the CRM during sync. Use this when you want to keep the mapping visible without importing it. Click to switch.",
        color: 'text-stone-500 bg-white border-dashed border-stone-300',
    },
};

const WRITE_POLICIES = ['fill_blank', 'always', 'ignore'];

function nextPolicy(policy) {
    const i = WRITE_POLICIES.indexOf(policy);
    return WRITE_POLICIES[(i + 1) % WRITE_POLICIES.length];
}

function policyForSelectedField(policy) {
    return WRITE_POLICIES.includes(policy) ? policy : 'fill_blank';
}

const TARGET_GROUPS = {
    account: [
        { label: 'Identity',         fields: ['name', 'legal_name', 'also_known_as', 'former_name', 'description', 'description_short', 'keywords', 'website'] },
        { label: 'Corporate',        fields: ['parent_company', 'top_level_parent'] },
        { label: 'Status',           fields: ['business_status', 'business_status_date', 'ownership_status', 'ownership_status_date', 'financing_status', 'financing_status_date'] },
        { label: 'Contact',          fields: ['email', 'phone', 'fax', 'linkedin_url', 'twitter_url', 'facebook_url'] },
        { label: 'Location',         fields: ['hq_location', 'address', 'address_line2', 'city', 'state', 'postcode', 'country', 'region', 'sub_region', 'hq_country'] },
        { label: 'Firmographics',    fields: ['industry', 'industry_group', 'industry_code', 'industry_vertical', 'all_industries', 'emerging_spaces', 'universe', 'num_employees', 'employee_range', 'employee_count_date', 'founded_year', 'investor_type'] },
        { label: 'Financials',       fields: ['annual_revenue', 'annual_revenue_range', 'total_raised', 'total_raised_native', 'total_raised_currency', 'funding_raised', 'enterprise_value', 'gross_profit', 'net_income', 'ebitda', 'ebit', 'net_debt', 'fiscal_period', 'fiscal_period_end'] },
        { label: 'Exchange',         fields: ['ticker', 'exchange'] },
        { label: 'Investor metrics', fields: ['aum', 'dry_powder', 'total_investments', 'total_exits', 'total_active_portfolio', 'total_funds_open', 'total_funds_closed', 'median_round_amount', 'median_valuation', 'min_fund_size', 'max_fund_size', 'investment_professional_count', 'other_investor_types', 'trade_associations'] },
    ],
    contact: [
        { label: 'Identity',         fields: ['first_name', 'last_name', 'middle_name', 'name_prefix', 'title', 'seniority', 'bio', 'gender', 'birth_year', 'education'] },
        { label: 'Contact',          fields: ['email', 'phone', 'mobile', 'fax', 'linkedin_url'] },
        { label: 'Primary company',  fields: ['primary_company_name', 'primary_company_type', 'primary_company_industry', 'primary_company_verticals', 'primary_company_website'] },
        { label: 'Notes',            fields: ['notes'] },
    ],
};

function prettyField(f) {
    if (!f) return '';
    return f.replace(/_/g, ' ').replace(/\b\w/g, m => m.toUpperCase()).replace(/\bUrl\b/, 'URL');
}

// ─── Field picker ─────────────────────────────────────────────────────────

function FieldPicker({ value, crmKey, unavailable = [], onChange, compact = false }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const inputRef = useRef(null);

    useEffect(() => {
        if (open) setTimeout(() => inputRef.current?.focus(), 50);
        else setSearch('');
    }, [open]);

    const groups = useMemo(() => {
        const all = TARGET_GROUPS[crmKey] || [];
        const q = search.toLowerCase();
        return all
            .map(g => ({ ...g, fields: g.fields.filter(f => !q || prettyField(f).toLowerCase().includes(q) || f.includes(q)) }))
            .filter(g => g.fields.length > 0);
    }, [crmKey, search]);

    const mapped = !!value;

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    className={`inline-flex items-center gap-1 rounded-lg border font-medium transition-colors
                        ${compact ? 'h-8 px-2.5 text-xs w-full' : 'h-9 px-3 text-sm w-full'}
                        ${mapped
                            ? 'bg-white border-stone-300 text-stone-800 hover:border-stone-400'
                            : 'bg-stone-50 border-dashed border-stone-300 text-stone-400 hover:text-stone-600 hover:border-stone-400'}`}
                >
                    <span className="flex-1 text-left truncate">
                        {mapped ? prettyField(value) : 'Not imported'}
                    </span>
                    <ChevronDown className="w-3 h-3 opacity-50 shrink-0" />
                </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="p-0 w-56 shadow-xl border-stone-200" sideOffset={4}>
                <div className="p-2 border-b border-stone-100">
                    <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400" />
                        <input
                            ref={inputRef}
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Search CRM fields…"
                            className="w-full pl-8 pr-3 py-1.5 text-xs rounded-md border border-stone-200 outline-none focus:border-stone-400 bg-stone-50"
                        />
                    </div>
                </div>
                <div className="overflow-y-auto p-1 max-h-72">
                    {mapped && (
                        <button
                            onClick={() => { onChange(null); setOpen(false); }}
                            className="w-full flex items-center gap-2 px-2.5 py-2 text-xs text-rose-600 hover:bg-rose-50 rounded-md transition-colors"
                        >
                            <X className="w-3.5 h-3.5" /> Remove mapping
                        </button>
                    )}
                    {groups.length === 0 && <p className="py-4 text-xs text-center text-stone-400">No fields match.</p>}
                    {groups.map(g => (
                        <div key={g.label}>
                            <div className="px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-stone-400 mt-1">
                                {g.label}
                            </div>
                            {g.fields.map(f => {
                                const conflict = unavailable.includes(f) && f !== value;
                                return (
                                    <button
                                        key={f}
                                        onClick={() => { onChange(f); setOpen(false); }}
                                        className={`w-full flex items-center justify-between gap-2 px-2.5 py-1.5 text-xs rounded-md transition-colors
                                            ${f === value ? 'bg-emerald-50 text-emerald-700' : 'text-stone-700 hover:bg-stone-100'}
                                            ${conflict ? 'opacity-60' : ''}`}
                                    >
                                        <span className="truncate">{prettyField(f)}</span>
                                        <span className="flex gap-1 items-center shrink-0">
                                            {conflict && <AlertTriangle className="w-3 h-3 text-amber-400" />}
                                            {f === value && <Check className="w-3 h-3 text-emerald-500" />}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </PopoverContent>
        </Popover>
    );
}

// ─── Policy connector arrow ───────────────────────────────────────────────
// Fixed-width bridge between PitchBook column → CRM field. Shafts grow only
// inside this track (not the full row), so the policy pill sits centered on a
// visible arrow without overstretching.

function PolicyArrow({ policy, mapped, conflict, onToggle, compact = false }) {
    const pm = POLICY_META[policy] || POLICY_META.fill_blank;
    const trackW = compact ? 'w-40' : 'w-48';

    if (!mapped) {
        return (
            <div className={`flex items-center shrink-0 ${trackW}`} aria-hidden>
                <div className="flex-1 min-w-[12px] border-t border-dashed border-stone-200" />
                <span className="w-0 h-0 shrink-0 -ml-px border-y-[3px] border-y-transparent border-l-[5px] border-l-stone-200" />
            </div>
        );
    }

    // Arrow = structure, not status → neutral grey. Rose only on real conflict.
    const shaft = conflict ? 'bg-rose-200' : 'bg-stone-300';
    const head  = conflict ? 'border-l-rose-300' : 'border-l-stone-400';

    return (
        <div className={`flex items-center shrink-0 ${trackW}`}>
            <div className={`flex-1 h-px min-w-[12px] ${shaft}`} />
            <button
                type="button"
                onClick={onToggle}
                title={pm.tip}
                className={`group/policy inline-flex items-center gap-1 rounded-full border font-medium shrink-0 transition-all hover:shadow-sm active:scale-95
                    ${compact ? 'h-5 px-2 text-[10px]' : 'h-6 px-2.5 text-[11px]'} ${pm.color}`}
            >
                {compact ? pm.short : pm.label}
                <Repeat2 className={`${compact ? 'w-2.5 h-2.5' : 'w-3 h-3'} ml-0.5 opacity-40 group-hover/policy:opacity-90 transition-opacity`} />
            </button>
            <div className={`flex-1 h-px min-w-[12px] ${shaft}`} />
            <span className={`-ml-px w-0 h-0 shrink-0 border-y-[4px] border-y-transparent border-l-[6px] ${head}`} />
        </div>
    );
}

// ─── Mapping row (normal + compact variants) ──────────────────────────────

function MappingRow({ col, sample, target, policy, conflict, crmKey, unavailable, onChange, compact = false }) {
    const mapped = !!target;
    const togglePolicy = () => onChange({ target, policy: nextPolicy(policy) });

    if (compact) {
        return (
            <div className={`group flex items-center gap-3 py-2 transition-colors min-w-0 rounded-lg
                ${mapped ? 'hover:bg-stone-50/80' : 'hover:bg-stone-50/40'}`}>

                {/* Source column */}
                <div className="w-28 min-w-0 shrink-0">
                    <code
                        className={`text-[11px] font-semibold block truncate leading-tight ${mapped ? 'text-stone-800' : 'text-stone-500'}`}
                        title={col}
                    >
                        {col}
                    </code>
                    {sample && (
                        <span className="text-[9px] text-stone-400 font-mono block truncate leading-tight" title={sample}>
                            {sample}
                        </span>
                    )}
                </div>

                {/* Connector arrow with policy on the shaft */}
                <PolicyArrow policy={policy} mapped={mapped} conflict={conflict} onToggle={togglePolicy} compact />

                {/* CRM field picker — rightmost */}
                <div className="w-36 shrink-0">
                    <FieldPicker
                        value={target}
                        crmKey={crmKey}
                        unavailable={unavailable}
                        onChange={f => onChange({ target: f, policy: f ? policyForSelectedField(policy) : 'fill_blank' })}
                        compact
                    />
                </div>

                {/* Remove on hover */}
                <button
                    onClick={() => mapped && onChange({ target: null, policy: 'fill_blank' })}
                    title="Remove mapping"
                    className={`w-5 h-5 rounded flex items-center justify-center shrink-0 transition-all
                        ${mapped
                            ? 'opacity-0 text-stone-300 hover:text-rose-500 hover:bg-rose-50 group-hover:opacity-100'
                            : 'invisible'}`}
                >
                    <X className="w-3 h-3" />
                </button>
            </div>
        );
    }

    // ── Full-width (single-column) layout ────────────────────────────────
    return (
        <div className={`group flex items-center gap-3 py-2.5 transition-colors rounded-lg
            ${mapped ? 'hover:bg-stone-50/80' : 'hover:bg-stone-50/40'}`}>

            {/* Source column */}
            <div className="w-48 shrink-0">
                <code className={`text-xs font-semibold block truncate ${mapped ? 'text-stone-800' : 'text-stone-500'}`} title={col}>
                    {col}
                </code>
                {sample && (
                    <span className="text-[10px] text-stone-400 font-mono block truncate mt-0.5" title={sample}>
                        {sample}
                    </span>
                )}
            </div>

            {/* Connector arrow with policy on the shaft */}
            <PolicyArrow policy={policy} mapped={mapped} conflict={conflict} onToggle={togglePolicy} />

            {/* CRM field picker — rightmost */}
            <div className="w-56 shrink-0">
                <FieldPicker
                    value={target}
                    crmKey={crmKey}
                    unavailable={unavailable}
                    onChange={f => onChange({ target: f, policy: f ? policyForSelectedField(policy) : 'fill_blank' })}
                />
            </div>

            {conflict && (
                <span className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-rose-50 border border-rose-200 text-[11px] text-rose-600 font-medium shrink-0">
                    <AlertTriangle className="w-3 h-3" /> Conflict
                </span>
            )}

            {mapped ? (
                <button
                    onClick={() => onChange({ target: null, policy: 'fill_blank' })}
                    title="Remove mapping"
                    className="flex justify-center items-center w-6 h-6 rounded-md opacity-0 transition-all text-stone-300 hover:text-rose-500 hover:bg-rose-50 group-hover:opacity-100 shrink-0"
                >
                    <X className="w-3.5 h-3.5" />
                </button>
            ) : <div className="w-6 shrink-0" />}
        </div>
    );
}

// ─── Main component ───────────────────────────────────────────────────────

export default function MappingEditor({ config, onSaved }) {
    const { getAuthHeader } = useAuth();
    const [activeEntity, setActiveEntity] = useState('company');
    const [draft, setDraft] = useState(() => JSON.parse(JSON.stringify(config.mapping || {})));
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState('');
    const [unmappedOpen, setUnmappedOpen] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [twoCol, setTwoCol] = useState(true);

    const sourceColumns  = config.schema?.source_columns || {};
    const baseline       = config.mapping || {};
    const samples        = config.samples || {};
    const changelog      = config.mapping_changelog || [];

    const entityMeta    = ENTITIES.find(e => e.key === activeEntity);
    const entityDraft   = draft[activeEntity] || {};
    const entitySamples = samples[activeEntity] || {};
    const allCols       = sourceColumns[activeEntity] || [];

    const targetCounts = useMemo(() => {
        const counts = {};
        for (const spec of Object.values(entityDraft)) {
            if (spec.target && spec.policy !== 'ignore') {
                counts[spec.target] = (counts[spec.target] || 0) + 1;
            }
        }
        return counts;
    }, [entityDraft]);

    const mappedCols    = allCols.filter(c => entityDraft[c]?.target);
    const unmappedCols  = allCols.filter(c => !entityDraft[c]?.target);
    const conflictCount = Object.values(targetCounts).filter(n => n > 1).length;

    const filteredMapped = useMemo(() => {
        const q = search.toLowerCase();
        return mappedCols.filter(c =>
            !q || c.toLowerCase().includes(q) ||
            (entitySamples[c] || '').toLowerCase().includes(q) ||
            prettyField(entityDraft[c]?.target || '').toLowerCase().includes(q)
        );
    }, [mappedCols, search, entitySamples, entityDraft]);

    const filteredUnmapped = useMemo(() => {
        const q = search.toLowerCase();
        return unmappedCols.filter(c =>
            !q || c.toLowerCase().includes(q) || (entitySamples[c] || '').toLowerCase().includes(q)
        );
    }, [unmappedCols, search, entitySamples]);

    // Split mapped rows into two columns
    const mid       = Math.ceil(filteredMapped.length / 2);
    const leftRows  = twoCol ? filteredMapped.slice(0, mid)  : filteredMapped;
    const rightRows = twoCol ? filteredMapped.slice(mid)     : [];

    const setMapping = (col, spec) =>
        setDraft(prev => {
            const next = JSON.parse(JSON.stringify(prev));
            next[activeEntity] = { ...(next[activeEntity] || {}), [col]: spec };
            return next;
        });

    const changes = useMemo(() => {
        const out = [];
        for (const ent of Object.keys(draft)) {
            for (const col of Object.keys(draft[ent] || {})) {
                const b = baseline[ent]?.[col] || {};
                const d = draft[ent][col] || {};
                if (b.target !== d.target || b.policy !== d.policy) out.push({ ent, col, from: b, to: d });
            }
        }
        return out;
    }, [draft, baseline]);

    const save = async () => {
        const fieldMapping = {};
        for (const { ent, col, to } of changes) {
            fieldMapping[ent] = fieldMapping[ent] || {};
            fieldMapping[ent][col] = { target: to.target || null, policy: to.policy };
        }
        setSaving(true);
        try {
            const res = await axios.put(`${API}/pitchbook/config/mapping`,
                { field_mapping: fieldMapping }, { headers: getAuthHeader() });
            if (res.data.warnings?.length) res.data.warnings.forEach(w => toast.warning(w));
            toast.success(`Mapping saved (v${res.data.field_mapping_version})`);
            onSaved?.();
        } catch {
            toast.error('Failed to save mapping');
        } finally {
            setSaving(false);
        }
    };

    const resetEntity = () =>
        setDraft(prev => ({ ...prev, [activeEntity]: JSON.parse(JSON.stringify(baseline[activeEntity] || {})) }));

    const switchEntity = (key) => {
        setActiveEntity(key);
        setSearch('');
        setUnmappedOpen(false);
    };

    // Helper: build row props
    const rowProps = (col, isUnmapped = false) => {
        const spec    = entityDraft[col] || {};
        const conflict = !isUnmapped && targetCounts[spec.target] > 1;
        return {
            key: col, col,
            sample:    entitySamples[col] || '',
            target:    isUnmapped ? null : (spec.target || null),
            policy:    spec.policy || 'fill_blank',
            conflict,
            crmKey:    entityMeta.crm,
            unavailable: Object.values(entityDraft)
                .filter(s => s.policy !== 'ignore')
                .map(s => s.target)
                .filter(t => t && t !== spec.target),
            onChange: s => setMapping(col, s),
        };
    };

    return (
        <div className="space-y-4">
            {/* ── Header ─────────────────────────────────────────────────── */}
            <div className="flex flex-wrap gap-3 justify-between items-center">
                <div className="flex flex-wrap gap-3 items-center">
                    <div className="inline-flex p-1 rounded-xl bg-stone-100">
                        {ENTITIES.map(({ key, label, Icon }) => {
                            const active = activeEntity === key;
                            const cnt = Object.values(draft[key] || {}).filter(c => c.target).length;
                            return (
                                <button key={key} onClick={() => switchEntity(key)}
                                    className={`flex items-center gap-2 px-3.5 py-1.5 text-sm font-medium rounded-lg transition-all
                                        ${active ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'}`}>
                                    <Icon className="w-4 h-4" />{label}
                                    <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-semibold
                                        ${active ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-200 text-stone-500'}`}>
                                        {cnt}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                    {conflictCount > 0 && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-rose-50 text-rose-600 text-xs font-medium border border-rose-100">
                            <AlertTriangle className="w-3.5 h-3.5" />
                            {conflictCount} conflict{conflictCount > 1 ? 's' : ''}
                        </span>
                    )}
                </div>
                <Popover open={historyOpen} onOpenChange={setHistoryOpen}>
                    <PopoverTrigger asChild>
                        <button className="flex items-center gap-1.5 text-xs text-stone-500 hover:text-stone-800 px-2.5 py-1.5 rounded-lg hover:bg-stone-100 transition-colors">
                            <History className="w-3.5 h-3.5" />History
                            {changelog.length > 0 && (
                                <span className="px-1.5 py-0.5 rounded-md bg-stone-200 text-stone-600 text-[10px] font-semibold">{changelog.length}</span>
                            )}
                        </button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="p-0 w-80">
                        <div className="px-3 py-2.5 border-b border-stone-100">
                            <p className="text-sm font-semibold text-stone-800">Mapping changelog</p>
                        </div>
                        <div className="overflow-y-auto p-2 space-y-1 max-h-64">
                            {changelog.length === 0 && <p className="px-2 py-3 text-xs text-center text-stone-400">No changes yet.</p>}
                            {[...changelog].reverse().map((c, i) => (
                                <div key={i} className="px-2.5 py-2 rounded-lg hover:bg-stone-50">
                                    <div className="flex justify-between items-center">
                                        <span className="text-xs font-medium text-stone-700">v{c.version}</span>
                                        <span className="text-[10px] text-stone-400">{c.at ? new Date(c.at).toLocaleDateString() : ''}</span>
                                    </div>
                                    <p className="text-[11px] text-stone-500 mt-0.5">{c.by_name || c.by}{c.note ? ` — ${c.note}` : ''}</p>
                                </div>
                            ))}
                        </div>
                    </PopoverContent>
                </Popover>
            </div>

            {/* ── Filter bar ─────────────────────────────────────────────── */}
            <div className="flex flex-wrap gap-2 items-center">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400" />
                    <Input value={search} onChange={e => setSearch(e.target.value)}
                        placeholder="Filter columns…"
                        className="pl-9 w-56 h-8 text-xs" />
                    {search && (
                        <button onClick={() => setSearch('')}
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600">
                            <X className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>

                {/* Layout toggle */}
                <div className="ml-auto inline-flex p-0.5 bg-stone-100 rounded-lg">
                    <button
                        onClick={() => setTwoCol(true)}
                        title="Two-column layout"
                        className={`p-1.5 rounded-md transition-all ${twoCol ? 'bg-white shadow-sm text-stone-700' : 'text-stone-400 hover:text-stone-600'}`}
                    >
                        <Columns2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                        onClick={() => setTwoCol(false)}
                        title="Single-column layout"
                        className={`p-1.5 rounded-md transition-all ${!twoCol ? 'bg-white shadow-sm text-stone-700' : 'text-stone-400 hover:text-stone-600'}`}
                    >
                        <AlignJustify className="w-3.5 h-3.5" />
                    </button>
                </div>
            </div>

            {/* ── Mapping list ─────────────────────────────────────────── */}
            <div>

                {/* Mapped rows */}
                {filteredMapped.length > 0 && (
                    twoCol ? (
                        <div className="grid grid-cols-1 gap-x-12 lg:grid-cols-2">
                            <div className="space-y-0.5">
                                {leftRows.map(col => (
                                    <MappingRow {...rowProps(col)} compact />
                                ))}
                            </div>
                            <div className="space-y-0.5">
                                {rightRows.map(col => (
                                    <MappingRow {...rowProps(col)} compact />
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-0.5">
                            {filteredMapped.map(col => (
                                <MappingRow {...rowProps(col)} />
                            ))}
                        </div>
                    )
                )}

                {/* Unmapped accordion — always full-width */}
                {filteredUnmapped.length > 0 && (
                    <div className={filteredMapped.length > 0 ? 'mt-6 pt-6 border-t border-stone-100' : ''}>
                        <button
                            onClick={() => setUnmappedOpen(v => !v)}
                            className="w-full flex items-center gap-2.5 py-2 text-xs font-medium text-stone-500 hover:text-stone-700 transition-colors"
                        >
                            {unmappedOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                            <Minus className="w-3 h-3 text-stone-300" />
                            <span>{filteredUnmapped.length} column{filteredUnmapped.length > 1 ? 's' : ''} not imported</span>
                            <span className="ml-auto text-[10px] text-stone-400">
                                {unmappedOpen ? 'Collapse' : 'Expand to map more'}
                            </span>
                        </button>
                        {unmappedOpen && (
                            twoCol ? (
                                <div className="grid grid-cols-1 gap-x-12 mt-2 lg:grid-cols-2">
                                    <div className="space-y-0.5">
                                        {filteredUnmapped.slice(0, Math.ceil(filteredUnmapped.length / 2)).map(col => (
                                            <MappingRow {...rowProps(col, true)} compact />
                                        ))}
                                    </div>
                                    <div className="space-y-0.5">
                                        {filteredUnmapped.slice(Math.ceil(filteredUnmapped.length / 2)).map(col => (
                                            <MappingRow {...rowProps(col, true)} compact />
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-0.5 mt-2">
                                    {filteredUnmapped.map(col => (
                                        <MappingRow {...rowProps(col, true)} />
                                    ))}
                                </div>
                            )
                        )}
                    </div>
                )}

                {/* Empty state — search with no matches */}
                {search && filteredMapped.length === 0 && filteredUnmapped.length === 0 && (
                    <div className="py-10 text-center">
                        <p className="text-sm text-stone-400">No columns match your search.</p>
                    </div>
                )}
            </div>

            {/* ── Legend ─────────────────────────────────────────────────── */}
            <div className="flex items-center gap-4 text-[10px] text-stone-400 px-0.5 flex-wrap">
                <span className="flex gap-1 items-center">
                    <Repeat2 className="w-3 h-3 text-stone-400" />
                    Click the badge on the arrow to switch how each field is written:
                </span>
                {[
                    { key: 'fill_blank', desc: '— only writes when the CRM field is empty' },
                    { key: 'always',     desc: '— overwrites the CRM value on every sync' },
                    { key: 'ignore',     desc: '— keeps the mapping visible but does not sync it' },
                ].map(({ key, desc }) => {
                    const pm = POLICY_META[key];
                    return (
                        <span key={key} className="flex items-center gap-1.5">
                            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px] font-medium ${pm.color}`}>
                                {pm.label}
                            </span>
                            {desc}
                        </span>
                    );
                })}
            </div>

            {/* ── Save bar ────────────────────────────────────────────────── */}
            {changes.length > 0 && (
                <div className="flex sticky bottom-0 gap-3 justify-between items-center px-4 py-3 bg-white rounded-xl border border-amber-200 shadow-lg">
                    <div className="flex gap-2 items-center min-w-0 text-sm text-stone-700">
                        <span className="flex justify-center items-center w-6 h-6 text-amber-700 bg-amber-100 rounded-full shrink-0">
                            <AlertTriangle className="w-3.5 h-3.5" />
                        </span>
                        <span><strong>{changes.length}</strong> unsaved change{changes.length > 1 ? 's' : ''}</span>
                    </div>
                    <div className="flex gap-2 items-center shrink-0">
                        <Button size="sm" variant="ghost" onClick={resetEntity} className="text-stone-500">
                            <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset
                        </Button>
                        <Button size="sm" onClick={save} disabled={saving}>
                            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
                            Save mapping
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
